/**
 * Select Component - Custom dropdown select element
 * 
 * Usage:
 * <select-component data-arrow data-continent-select>
 *   <div class="select-component__trigger">
 *     <span>Select an option</span>
 *   </div>
 *   <div class="select-component__dropdown">
 *     <p class="select-component__option" disabled>Label</p>
 *     <p class="select-component__option" option-value="value1" data-country-name="USA" data-iso-code="US">Option 1</p>
 *     <p class="select-component__option" option-value="value2" selected>Option 2</p>
 *     <p class="select-component__option" option-value="value3">Option 3</p>
 *   </div>
 * </select-component>
 * 
 * Attributes:
 * - data-arrow: Optional. If present, shows a dropdown arrow icon
 * - data-*: Any data attributes on the component will be included in the event
 * 
 * Events:
 * - 'select-changed': Fired when an option is selected
 *   detail: {
 *     value: string,           // The option-value or text content
 *     text: string,            // The visible text of the option
 *     option: HTMLElement,     // Reference to the selected option element
 *     optionData: object,      // All data-* attributes from the selected option
 *     selector: {              // Information about which select component
 *       className: string,
 *       id: string|null,
 *       ...dataset             // All data-* attributes from the select-component
 *     }
 *   }
 * 
 * Example listener:
 * document.querySelector('select-component').addEventListener('select-changed', (e) => {
 *   console.log('Selected value:', e.detail.value);
 *   console.log('Selected text:', e.detail.text);
 *   console.log('Option datasets:', e.detail.optionData);
 *   console.log('Selector info:', e.detail.selector);
 *   console.log('Country name:', e.detail.optionData.countryName);
 *   console.log('ISO code:', e.detail.optionData.isoCode);
 * });
 */

if (customElements.get('select-component') === undefined) {
  class SelectComponent extends HTMLElement {
    constructor() {
      super();
      this._isOpen = false;
      this._highlightedIndex = -1;
      this._filterText = '';
      this._filterTimeout = null;
      this._boundHandleClick = this._handleClick.bind(this);
      this._boundHandleOutsideClick = this._handleOutsideClick.bind(this);
      this._boundHandleKeydown = this._handleKeydown.bind(this);
      this._boundHandleKeyup = this._handleKeyup.bind(this);
    }

    connectedCallback() {
      this._init();
    }

    disconnectedCallback() {
      this._cleanup();
    }

    // Initialize component
    _init() {
      this.trigger = this.querySelector('.select-component__trigger');
      this.dropdown = this.querySelector('.select-component__dropdown');
      this._updateOptions();
      
      if (!this.trigger || !this.dropdown) {
        console.error('select-component: Missing required elements');
        return;
      }

      // Make trigger focusable
      if (!this.trigger.hasAttribute('tabindex')) {
        this.trigger.setAttribute('tabindex', '0');
      }

      this._addArrowIfNeeded();
      this._setInitialValue();
      this._attachEventListeners();
    }

    // Update options list (call when options change dynamically)
    _updateOptions(applyHidingLogic = true) {
      this.options = this.querySelectorAll('.select-component__option:not([disabled])');
      this._resetHighlight();

      // Only apply hiding logic during initialization, not when opening/refreshing
      if (applyHidingLogic) {
        this._applyHideOnSingleOption();
      }
    }

    _applyHideOnSingleOption() {
      if (!this.hasAttribute('data-hide-on-one-option')) {
        return false;
      }

      // Only apply hiding logic on first initialization, not on every click
      // Check if we've already determined this should be hidden
      if (this.hasAttribute('data-single-option-hidden')) {
        return true; // Already hidden, return true to indicate it was hidden
      }

      const shouldHide = this.options.length <= 1;

      if (shouldHide) {
        this.style.display = 'none';
        this.setAttribute('aria-hidden', 'true');
        this.setAttribute('data-single-option-hidden', 'true'); // Mark as hidden
      } else {
        this.style.display = 'block';
        this.removeAttribute('aria-hidden');
      }

      return shouldHide;
    }

    // Public method to refresh options (for dynamic content)
    refreshOptions() {
      this._updateOptions();
    }

    // Add arrow icon if data-arrow attribute is present
    _addArrowIfNeeded() {
      if (this.hasAttribute('data-arrow')) {
        const existingArrow = this.trigger.querySelector('svg');
        
        if (!existingArrow) {
          const arrow = createArrowSVG();
          this.trigger.appendChild(arrow);
        }
      }
    }

    // Set initial value from selected option
    _setInitialValue() {
      const selectedOption = this.querySelector('.select-component__option[selected]');
      
      if (selectedOption) {
        this._updateTriggerText(selectedOption.textContent.trim());
      }
    }

    // Attach event listeners
    _attachEventListeners() {
      this.trigger.addEventListener('click', this._boundHandleClick);
      this.trigger.addEventListener('keydown', this._boundHandleKeydown);
      this.trigger.addEventListener('keyup', this._boundHandleKeyup);
      this._boundHandleBlur = this._handleBlur.bind(this);
      this.trigger.addEventListener('blur', this._boundHandleBlur);
      
      for (const option of this.options) {
        option.addEventListener('click', (e) => this._handleOptionClick(e, option));
      }
    }

    // Handle trigger click
    _handleClick(event) {
      event.stopPropagation();
      this._toggle();
    }

    // Handle option click
    _handleOptionClick(event, option) {
      event.stopPropagation();
      
      // Update selected state
      this.querySelectorAll('.select-component__option[selected]').forEach(opt => {
        opt.removeAttribute('selected');
      });
      
      option.setAttribute('selected', '');
      
      // Update trigger display
      this._updateTriggerText(option.textContent.trim());
      
      // Collect all dataset values from the option
      const optionData = { ...option.dataset };
      
      // Collect selector identifiers
      const selectorData = {
        dataset: { ...this.dataset },
        className: this.className,
        id: this.id || null
      };
      
      // Dispatch custom event with comprehensive data
      const selectedEvent = new CustomEvent('select-changed', {
        detail: {
          value: option.getAttribute('option-value') || option.textContent.trim(),
          text: option.textContent.trim(),
          option: option,
          optionData: optionData,
          selector: selectorData
        },
        bubbles: true
      });
      
      this.dispatchEvent(selectedEvent);
      
      // Close dropdown
      this._close();
    }

    // Handle outside click
    _handleOutsideClick() {
      this._close();
    }

    // Handle blur (when focus leaves the trigger)
    _handleBlur(e) {
      // Close dropdown when focus leaves, unless moving to something within this component
      setTimeout(() => {
        const activeElement = document.activeElement;
        const isFocusWithinComponent = this.contains(activeElement);
        
        if (!isFocusWithinComponent && this._isOpen) {
          this._close();
        }
      }, 10);
    }

    // Toggle dropdown
    _toggle() {
      if (this._isOpen) {
        this._close();
      } else {
        this._open();
      }
    }

    // Open dropdown
    _open() {
      // Close all other selects
      closeAllSelects(this);

      // Update options in case they changed, but don't apply hiding logic
      this._updateOptions(false);

      this._isOpen = true;
      this.classList.add('select-component--open');
      
      // Reset filter and highlight first option
      this._filterText = '';
      this._clearFilter();
      
      // Highlight first option if available
      if (this.options.length > 0) {
        this._highlightIndex(0);
      }
      
      document.addEventListener('click', this._boundHandleOutsideClick);
    }

    // Close dropdown
    _close() {
      this._isOpen = false;
      this.classList.remove('select-component--open');
      
      // Clear filter and reset highlight
      this._filterText = '';
      this._resetHighlight();
      this._clearFilter();
      
      document.removeEventListener('click', this._boundHandleOutsideClick);
    }

    // Update trigger text
    _updateTriggerText(text) {
      const span = this.trigger.querySelector('span');
      
      if (span) {
        span.textContent = text;
      }
    }

    // Handle keyboard events
    _handleKeydown(e) {
      // Allow Tab to work normally for navigation between selects
      if (e.key === 'Tab') {
        return;
      }
      
      // When dropdown is closed, open it on Enter, Space, or Arrow keys
      if (!this._isOpen) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          this._open();
          // Highlight first option when opening with keyboard
          if (this.options.length > 0) {
            this._highlightIndex(0);
          }
        }
        return;
      }

      // Handle keys when dropdown is open
      if (this._isOpen) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            this._navigateOptions(1);
            break;
          case 'ArrowUp':
            e.preventDefault();
            this._navigateOptions(-1);
            break;
          case 'Enter':
            e.preventDefault();
            this._selectHighlighted();
            break;
          case 'Escape':
            e.preventDefault();
            this._close();
            this.trigger.focus();
            break;
          case 'Tab':
            // Allow Tab to close dropdown and move to next element
            this._close();
            break;
          default:
            // For letter keys, handle in keyup to avoid interfering with other keys
            break;
        }
      }
    }

    // Handle keyup for letter filtering
    _handleKeyup(e) {
      // Only handle letter keys when dropdown is open
      if (!this._isOpen) return;

      // Check if it's a letter or number (a-z, A-Z, 0-9)
      const key = e.key;
      if (key.length === 1 && /[a-zA-Z0-9]/.test(key)) {
        e.preventDefault();
        
        // Clear previous timeout
        if (this._filterTimeout) {
          clearTimeout(this._filterTimeout);
        }

        // Add to filter text
        this._filterText += key.toLowerCase();

        // Apply filter
        this._filterOptions(this._filterText);

        // Reset filter text after 1 second of no typing
        this._filterTimeout = setTimeout(() => {
          this._filterText = '';
        }, 1000);
      }
    }

    // Filter options based on text
    _filterOptions(filterText) {
      if (!filterText) {
        this._clearFilter();
        this._resetHighlight();
        return;
      }

      const visibleOptions = [];
      
      for (const option of this.options) {
        const optionText = option.textContent.trim().toLowerCase();
        const isMatch = optionText.startsWith(filterText);
        
        // Show/hide option
        if (isMatch) {
          option.style.display = '';
          option.removeAttribute('data-filtered');
          visibleOptions.push(option);
        } else {
          option.style.display = 'none';
          option.setAttribute('data-filtered', 'true');
        }
      }

      // Highlight first visible option
      if (visibleOptions.length > 0) {
        const firstIndex = Array.from(this.options).indexOf(visibleOptions[0]);
        this._highlightIndex(firstIndex);
      } else {
        this._resetHighlight();
      }
    }

    // Clear filter
    _clearFilter() {
      for (const option of this.options) {
        option.style.display = '';
        option.removeAttribute('data-filtered');
      }
      
      // If dropdown is open and we have options, ensure first option is highlighted if no current highlight
      if (this._isOpen && this.options.length > 0) {
        if (this._highlightedIndex === -1 || this._highlightedIndex >= this.options.length) {
          this._highlightIndex(0);
        }
      }
    }

    // Navigate options with arrow keys
    _navigateOptions(direction) {
      // Store current highlighted index before updating options
      const previousHighlightedIndex = this._highlightedIndex;
      
      // Update options list (this will reset highlight, but we preserve the index above)
      this.options = this.querySelectorAll('.select-component__option:not([disabled])');
      
      // Get visible (non-filtered) options - check both data-filtered attribute and display style
      const visibleOptions = Array.from(this.options).filter(opt => {
        const isFiltered = opt.hasAttribute('data-filtered') && opt.getAttribute('data-filtered') === 'true';
        const isHidden = opt.style.display === 'none';
        return !isFiltered && !isHidden;
      });

      if (visibleOptions.length === 0) return;

      // Find current highlighted index in visible options
      let currentVisibleIndex = -1;
      if (previousHighlightedIndex >= 0 && previousHighlightedIndex < this.options.length) {
        const currentHighlighted = this.options[previousHighlightedIndex];
        // Check if this option is actually visible
        const isFiltered = currentHighlighted.hasAttribute('data-filtered') && currentHighlighted.getAttribute('data-filtered') === 'true';
        const isHidden = currentHighlighted.style.display === 'none';
        
        if (!isFiltered && !isHidden) {
          currentVisibleIndex = visibleOptions.indexOf(currentHighlighted);
        }
      }

      // If no current highlight or current highlight is not in visible options, start from beginning/end
      let newVisibleIndex;
      if (currentVisibleIndex === -1) {
        // No current highlight - start from first (down) or last (up)
        newVisibleIndex = direction > 0 ? 0 : visibleOptions.length - 1;
      } else {
        // Calculate new index from current position
        newVisibleIndex = currentVisibleIndex + direction;
        
        // Wrap around
        if (newVisibleIndex < 0) {
          newVisibleIndex = visibleOptions.length - 1;
        } else if (newVisibleIndex >= visibleOptions.length) {
          newVisibleIndex = 0;
        }
      }

      // Highlight new option
      const newOption = visibleOptions[newVisibleIndex];
      const newIndex = Array.from(this.options).indexOf(newOption);
      this._highlightIndex(newIndex);
    }

    // Highlight option at index
    _highlightIndex(index) {
      // Remove previous highlight
      this._resetHighlight();

      if (index >= 0 && index < this.options.length) {
        this._highlightedIndex = index;
        const option = this.options[index];
        option.setAttribute('data-highlighted', 'true');
        option.setAttribute('aria-selected', 'true');
        
        // Scroll into view
        option.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    // Reset highlight
    _resetHighlight() {
      for (const option of this.options) {
        option.removeAttribute('data-highlighted');
        option.removeAttribute('aria-selected');
      }
      this._highlightedIndex = -1;
    }

    // Select highlighted option
    _selectHighlighted() {
      if (this._highlightedIndex >= 0 && this._highlightedIndex < this.options.length) {
        const option = this.options[this._highlightedIndex];
        this._handleOptionClick(new Event('click'), option);
      }
    }

    // Cleanup
    _cleanup() {
      this.trigger?.removeEventListener('click', this._boundHandleClick);
      this.trigger?.removeEventListener('keydown', this._boundHandleKeydown);
      this.trigger?.removeEventListener('keyup', this._boundHandleKeyup);
      document.removeEventListener('click', this._boundHandleOutsideClick);
      
      if (this._filterTimeout) {
        clearTimeout(this._filterTimeout);
      }
    }
  }

  // Utility function to close all other selects
  const closeAllSelects = (exceptElement) => {
    const allSelects = document.querySelectorAll('select-component');
    
    for (const select of allSelects) {
      if (select !== exceptElement && select._isOpen) {
        select._close();
      }
    }
  };

  // Utility function to create arrow SVG
  const createArrowSVG = () => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '12');
    svg.setAttribute('height', '8');
    svg.setAttribute('viewBox', '0 0 12 8');
    svg.setAttribute('fill', 'none');
    
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M1 1.5L6 6.5L11 1.5');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    
    svg.appendChild(path);
    return svg;
  };

  customElements.define('select-component', SelectComponent);
}

